import React, { useMemo } from 'react';

import { useCurrentCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';

import { stepDefinitions } from 'pages/checkout/steps/stepDefinitions';

import { ProgressMarker } from './components/ProgressMarker';
import { Divider, ProgressContainer, ProgressWrapper } from './Progress-styled-components';

interface ProgressMarkerDefinition {
  name: string;
  id: string;
  stepNumber: number;
  isHighlighted: boolean;
}

const renderProgressContent = (progressMarkerDefinitions: ProgressMarkerDefinition[]) =>
  progressMarkerDefinitions.reduce((acc: React.ReactNode[], progressMarkerDefinition, index) => {
    const progressMarker = (
      <ProgressMarker
        key={progressMarkerDefinition.id}
        stepName={progressMarkerDefinition.name}
        stepNumber={progressMarkerDefinition.stepNumber}
        isHighlighted={progressMarkerDefinition.isHighlighted}
      />
    );

    if (index === 0) {
      return [progressMarker];
    }

    const divider = (
      <Divider
        key={`separator-${index}`}
        $isActive={progressMarkerDefinition.isHighlighted}
      />
    );

    return [...acc, divider, progressMarker];
  }, [] as React.ReactNode[]);

export const Progress = () => {
  const { currentStep } = useCurrentCheckoutStep();
  const currentStepIndex = useMemo(() => stepDefinitions.indexOf(currentStep), [currentStep]);

  const progressMarkerDefinitions = useMemo(
    () => stepDefinitions
      .filter((stepDefinition) => stepDefinition.showInProgress)
      .map((stepDefinition, index) => {
        // We need this step's index from the full list of steps,
        // as that's what currentStepIndex is based on.
        const indexInApplicableSteps = stepDefinitions.indexOf(stepDefinition);

        return {
          id: stepDefinition.id,
          name: stepDefinition.name,
          stepNumber: index + 1,
          isHighlighted: indexInApplicableSteps <= currentStepIndex,
        } as ProgressMarkerDefinition;
      }),
    [currentStepIndex],
  );

  return (
    <ProgressWrapper>
      <ProgressContainer>
        {renderProgressContent(progressMarkerDefinitions)}
      </ProgressContainer>
    </ProgressWrapper>
  );
};
